import type { Dialog as BaseDialog } from '@base-ui/react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ForumAuthor as TForumAuthor, ForumComment as TForumComment } from '~/api/forum/types';

import { useUpdateForumComment } from '~/api/forum/useUpdateForumComment';
import { useUser } from '~/api/user';
import { AlertDialog, Button, Menu, RichTextRenderer } from '~/components';
import { toast } from '~/utils/toast';

import type { ForumCommentFieldValues } from './ForumCommentEditorField';

import { ConfirmDeleteForumCommentDialog } from './ConfirmDeleteForumCommentDialog';
import { EditForumCommentForm } from './EditForumCommentForm';
import { ForumAuthor } from './ForumAuthor';
import { cardStyle, contentStyle, headerStyle } from './ForumComment.css';

type Props = {
  authorDialogHandle: BaseDialog.Handle<TForumAuthor>;
  comment: TForumComment;
};

export const ForumComment = ({ authorDialogHandle, comment }: Props) => {
  const { data: user } = useUser();
  const updateCommentMutation = useUpdateForumComment(comment.id);
  const [showForm, setShowForm] = useState(false);

  const confirmDeleteHandle = AlertDialog.useHandle();
  const { t } = useTranslation(['common', 'forum', 'validation']);

  const onSubmit = (data: ForumCommentFieldValues) => {
    updateCommentMutation.mutate(
      { content: JSON.stringify(data.content) },
      {
        onSuccess: () => {
          setShowForm(false);
          toast.success({ description: t('forum:alerts.editComment.success') });
        },
        onError: () => toast.danger({ description: t('forum:alerts.editComment.error') }),
      },
    );
  };

  return (
    <>
      <article className={cardStyle}>
        <header className={headerStyle}>
          <ForumAuthor
            author={comment.author}
            createdAt={comment.createdAt}
            dialogHandle={authorDialogHandle}
            editedAt={comment.editedAt}
          />

          {comment.author.communityProfileId === user.communityProfile.id && user.communityProfile.enabled && (
            <Menu.Root>
              <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} emphasis="tertiary" icon="MoreVert" />} />

              <Menu.List>
                <Menu.Item icon="Edit" onClick={() => setShowForm(true)}>
                  {t('common:actions.edit')}
                </Menu.Item>

                <Menu.Item icon="Trash" onClick={() => confirmDeleteHandle.open(null)}>
                  {t('common:actions.delete')}
                </Menu.Item>
              </Menu.List>
            </Menu.Root>
          )}
        </header>

        <div className={contentStyle}>
          {showForm ? (
            <EditForumCommentForm
              comment={comment}
              loading={updateCommentMutation.isPending}
              onCancel={() => setShowForm(false)}
              onSubmit={onSubmit}
            />
          ) : (
            <RichTextRenderer content={comment.content} />
          )}
        </div>
      </article>

      <AlertDialog.Root handle={confirmDeleteHandle}>
        <ConfirmDeleteForumCommentDialog comment={comment} onClose={() => confirmDeleteHandle.close()} />
      </AlertDialog.Root>
    </>
  );
};
